import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { QuoteRequestState } from '@app/shared';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { ContractService } from '@app/shared/services/contract.service';
import { WizardService } from '@app/wizard-bar/services/wizard.service';
import { BehaviorSubject, Subscription, from } from 'rxjs';
import { angularMaterialRenderers } from '@jsonforms/angular-material';
import { LoadingModalComponent, LoadingModalData } from '../loading-modal/loading-modal.component';
import { createAjv } from '@jsonforms/core';
import { DatePickerRenderer, DateControlRendererTester } from './CustomControls/date-picker-control';
import { AddressLookupControl, AddressLookupRendererTester } from './CustomControls/smartystreet-address-lookup-control';
import { ContractSchema } from '@app/shared/models/ContractSchema';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { QuoteConfiguration } from '@app/shared/models/customer-details';
import { CustomService } from '@app/custom/services/custom.service';
import { SaleType } from '@app/shared/models/sale-type';
import { UdFile } from '@app/filemanagement/UdFile';
import LogRocket from 'logrocket';
import { ContractTypeControlRenderer, ContractTypeControlRendererTester } from './CustomControls/contract-type-control';
import { CustomerType } from '@app/shared/models/customer-type';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { creditCheckViewModelSelector, isReadOnlySelector, successfulCreditCheckSelector } from '@app/state/app.selectors';
import { SetCustomerDetails, SetQuoteRequestState } from '@quoting/state/actions/quoting-module.actions';
import { Quote } from '@quoting/models/quote';
import { UsAddressLookupService } from '@app/shared/services/us-address-lookup.service';
import { Logger } from '@app/logging.service';
import { UtilityAccount } from '@app/shared/models/UtilityAccount';
import { Contract } from '@app/shared/models/Contract';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss'],
})
export class ContractDetailsComponent implements OnInit, OnDestroy {
  public loading = true;
  public quoteRequestId: number;
  public quoteRequestState: QuoteRequestState;
  public quoteConfiguration: QuoteConfiguration;
  public signedContractDocuments: Array<UdFile> = new Array<UdFile>();

  renderers = [
    ...angularMaterialRenderers,
    { renderer: ContractTypeControlRenderer, tester: ContractTypeControlRendererTester },
    { renderer: DatePickerRenderer, tester: DateControlRendererTester },
    { renderer: AddressLookupControl, tester: AddressLookupRendererTester },
  ];

  contractGenerated = false;
  showContractNotUploadedLabel = false;
  isNewContract: any = false;
  isFormValid = false;
  isUseDefaultAddressChecked = true;
  displaySchema: any;
  schemaId: number;
  schema: any;
  data: any;
  customerType: any;

  validationSettings = createAjv({
    schemaId: 'auto',
    allErrors: true,
    jsonPointers: true,
    errorDataPath: 'property',
  });
  utilityAccounts: any[];
  public organizationName: any;
  private isCreditCheckQuote$: Subscription;
  public isCreditCheckQuote: boolean;
  private successfulCreditCheck$: Subscription;
  public successfulCreditCheck: boolean;
  public invokedLookup: boolean;
  public companyZipCode: string;
  public companySearchResults$: Subscription;
  public companySearchError$: Subscription;
  public quote: Quote;
  public billingZipCode: string;
  public lookup$: Subscription;
  private logger: Logger;
  private isReadOnly$: Subscription;
  public isReadOnly: boolean;
  public contract: Contract

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private wizardService: WizardService,
    private customService: CustomService,
    private quoteRequestService: QuoteRequestService,
    private contractService: ContractService,
    private store: Store<AppState>,
    private usLookup: UsAddressLookupService,
    private cd: ChangeDetectorRef,
  ) {
    this.logger = Logger.for(this);
  }

  ngOnDestroy(): void {
    this.isCreditCheckQuote$?.unsubscribe();
    this.successfulCreditCheck$?.unsubscribe();
    this.lookup$?.unsubscribe();
    this.isReadOnly$.unsubscribe();
  }

  async ngOnInit() {
    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });

    this.wizardService.bindStep(this.route, (args) => from(this.save()));

    const route = await this.route.snapshot.params;
    this.quoteRequestId = route.quoteRequestId;

    const dialog = this.dialog.open(LoadingModalComponent, {
      width: '25%',
      disableClose: true,
      data: {
        message: `Loading Contract Details`,
        unitOfWork: from(this.load()),
      } as LoadingModalData,
    });

    dialog
      .afterClosed()
      .toPromise()
      .then()
      .catch((error) => console.log(error));
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  async load(): Promise<any> {
    this.quoteRequestState = await this.quoteRequestService.getQuoteRequestState(this.quoteRequestId).toPromise();
    this.quoteConfiguration = await this.customService.getQuoteConfiguration(this.quoteRequestId).toPromise();
    this.utilityAccounts = await this.quoteRequestService.getUtilityAccounts(this.quoteRequestId).toPromise();

    this.setCustomerAddress();

    this.store.dispatch(new SetCustomerDetails(this.quoteRequestId, this.quoteConfiguration.customerDetails));
    this.store.dispatch(new SetQuoteRequestState(this.quoteRequestId, this.quoteRequestState));

    this.utilityAccounts.sort((a, b) => (a.newContractStartDate < b.newContractStartDate ? -1 : 1));

    const signedContractDocument = await this.quoteRequestService.getSignedContractDocument(this.quoteRequestId).toPromise();
    if (signedContractDocument) {
      this.signedContractDocuments = [signedContractDocument];
    }

    this.customerType = this.quoteConfiguration?.customerDetails?.customerType;

    this.contract = await this.getContractData(this.quoteRequestId, this.quoteConfiguration);

    if (this.isNewContract) {
        const contractSchema = await this.getContractSchema(this.customerType);
        this.schemaId = contractSchema.id;
        this.schema = JSON.parse(contractSchema.jsonSchema);
        this.displaySchema = JSON.parse(contractSchema.displaySchema);
    }
    else {
      this.schemaId = this.contract.contractSchemaId;
      this.schema = JSON.parse(this.contract.jsonSchema);
      this.displaySchema = JSON.parse(this.contract.displaySchema);
    }
    this.data = JSON.parse(this.contract.data);
    const minStartDate = new Date(this.utilityAccounts[0].newContractStartDate);
    const maxStartDate = new Date(minStartDate.getFullYear(), minStartDate.getMonth() + 1, 0);

    this.setNewContractStartDateOptions(this.displaySchema, minStartDate, maxStartDate);
    const contractGeneratedStatuses = [
      QuoteRequestStatus.PendingEnrollment,
      QuoteRequestStatus.PendingContract,
      QuoteRequestStatus.ContractSigned,
      QuoteRequestStatus.ContractDropPending,
      QuoteRequestStatus.ContractDropped,
      QuoteRequestStatus.ContractExpired,
      QuoteRequestStatus.ContractLive,
      QuoteRequestStatus.ContractExpiring,
      QuoteRequestStatus.DocuSignCompleted,
      QuoteRequestStatus.Enrolled,
      QuoteRequestStatus.PartiallyEnrolled
    ];

    this.contractGenerated = contractGeneratedStatuses.includes(QuoteRequestStatus[this.quoteRequestState.status]);

    this.isCreditCheckQuote$ = this.store.select(creditCheckViewModelSelector).subscribe((result) => {
      this.isCreditCheckQuote = result.isVisible;
    });

    this.successfulCreditCheck$ = this.store.select(successfulCreditCheckSelector).subscribe((result) => {
      this.successfulCreditCheck = result;
    });

    this.loading = false;

    const translateErrorMessages = (): string => `if (vErrors) {
      vErrors = vErrors.map((v) => {
        if (
          (v.keyword === 'pattern' && v.schemaPath === '#/definitions/contact/properties/phone/pattern') ||
          (v.keyword === 'pattern' && v.schemaPath === '#/properties/contact/properties/phone/pattern') ||
          (v.keyword === 'pattern' && v.schemaPath === '#/properties/billingContact/properties/phone/pattern')
        ) {
            v.message = "Phone number must be in a numeric format with 10 digits";
      }

        return v;
      });
    }`;
    this.validationSettings.addKeyword('customErrorMessages', {
      inline: translateErrorMessages,
      statements: true,
      valid: true,
      errors: 'full',
    });
    return true;
  }

  setCustomerAddress(){
    this.quoteConfiguration.customerDetails.location.addressLine1 = this.utilityAccounts[0].address.addressLine1;
    this.quoteConfiguration.customerDetails.location.addressLine2 = this.utilityAccounts[0].address.addressLine2;
    this.quoteConfiguration.customerDetails.location.city = this.utilityAccounts[0].address.city;
    this.quoteConfiguration.customerDetails.location.state = this.utilityAccounts[0].address.state;
    this.quoteConfiguration.customerDetails.location.zipCode = this.utilityAccounts[0].address.zipCode;
  }

  setNewContractStartDateOptions(obj: any, minStartDate: Date, maxStartDate: Date) {
    if (obj.type === "Control" && obj.scope === "#/properties/newContractStartDate") {
        obj.options = {
          minDate: minStartDate,
          maxDate: maxStartDate
        };
    } else if (obj.elements) {
        obj.elements.forEach(element => this.setNewContractStartDateOptions(element, minStartDate, maxStartDate));
    }
  }

  async getContractData(quoteRequestId: number, quoteConfiguration: QuoteConfiguration): Promise<Contract> {

    try {
      // this.save();
      const contract = await this.contractService.getContract(quoteRequestId);
      this.isNewContract = false;
      return contract;
    } catch (error) {
      console.log(error);
      return await this.createDefaultContract(quoteConfiguration);
    }
  }

  async getContractSchema(jsonSchemaType: string): Promise<ContractSchema> {

    try {
        return await this.contractService.getSchema(jsonSchemaType);
    } catch (error) {
      console.log(error);
      return await this.contractService.createSchema();
    }
  }

  getContractStartDate() {
    const newContractStartDate = new Date(this.utilityAccounts[0].newContractStartDate);
    return <string>(newContractStartDate.getFullYear() + '-' + ('0' + (newContractStartDate.getMonth() + 1)).slice(-2) + '-01');
  }

  createDefaultContract(quoteConfig: QuoteConfiguration) {
    this.isNewContract = true;
    let newContract : Contract = {  id: 0, data: '', quoteRequestId: this.quoteRequestId, contractSchemaId: this.schemaId, jsonSchema: '', displaySchema: '', dgsMappingSchema: '' };
    newContract.data =  JSON.stringify({
      saleType: quoteConfig?.saleType ?? SaleType.Acquisition,
      newContractStartDate: this.getContractStartDate(),
      contact: {
        phone: quoteConfig.customerDetails?.telephoneNumber ?? '',
        firstName: quoteConfig.customerDetails?.customerName?.customerFirstName ?? '',
        lastName: quoteConfig.customerDetails?.customerName?.customerLastName ?? '',
      },
      company: {
        name: quoteConfig.customerDetails?.organisationName,
        companyAddress: {
          state: quoteConfig.customerDetails?.location?.state ?? '',
          city: quoteConfig.customerDetails?.location?.city ?? '',
          zipCode: quoteConfig.customerDetails?.location?.zipCode ?? '',
          addressLine1: quoteConfig.customerDetails?.location?.addressLine1 ?? '',
          addressLine2: quoteConfig.customerDetails?.location?.addressLine2 ?? '',
        },
      },
      county: "",
      customer: {
        firstName: quoteConfig.customerDetails?.customerName?.customerFirstName,
        lastName: quoteConfig.customerDetails?.customerName?.customerLastName,
        customerAddress: {
          state: quoteConfig.customerDetails?.location?.state ?? '',
          city: quoteConfig.customerDetails?.location?.city ?? '',
          zipCode: quoteConfig.customerDetails?.location?.zipCode ?? '',
          addressLine1: quoteConfig.customerDetails?.location?.addressLine1 ?? '',
          addressLine2: quoteConfig.customerDetails?.location?.addressLine2 ?? '',
        },
      },
      useDefaultAddress: true,
      billingContact: {},
      summaryBilling: false
    });
    return newContract;
  }

  setOrganizationName($event: any) {
    if (this.customerType === CustomerType.Residential) {
      this.organizationName = `${$event.customer.firstName ?? ''} ${$event.customer.lastName ?? ''}`
    } else {
      this.organizationName = `${$event.company.name ?? ''}`
    }
  }

  async setData($event) {
    this.setOrganizationName($event);

    if ($event.billingAddressSameAsCustomerDetails) {
      $event.billingAddress = $event.customer.customerAddress;
      $event.firstName = $event.customer.firstName;
      $event.lastName = $event.customer.lastName;
    } else if ($event.billingAddressSameAsCompanyAddress) {
      $event.billingAddress = $event.company.companyAddress;
    }

    if (!this.contractGenerated && $event.contractType === 'None') {
      this.contractGenerated = true;
    }

    if ($event.billingContactSameAsContact) {
      if (this.customerType === CustomerType.Residential) {
        $event.billingContact.firstName = $event.customer.firstName;
        $event.billingContact.lastName = $event.customer.lastName;
        $event.billingContact.email = $event.contact.email;
        $event.billingContact.phone = $event.contact.phone;
      } else {
        $event.billingContact = $event.contact;
      }
    }

    if ($event.summaryBilling){
       if (!this.schema.required.includes("billingAccount")) {
          if ($event.billingAccount == '') {
            $event.billingAccount = null;
          }
          this.schema.required.push("billingAccount");
        }
      } else {
        $event.billingAccount = '';
        this.schema.required = this.schema.required.filter((item) => item !== "billingAccount");
     }

    this.data = $event;
    this.cd.detectChanges();
    this.isFormValid = await this.validateForm(this.schema, this.data);
    this.companyZipCode = this.data?.company?.companyAddress?.zipCode ?? '';
    this.billingZipCode = this.data.billingAddress?.zipCode ?? '';
    this.isUseDefaultAddressChecked = this.data.useDefaultAddress;

    if (this.billingZipCode.length === 5) {
      this.searchForState(this.billingZipCode);
    }
  }

  contractGeneratedHandler($event) {
    this.isNewContract = false;
    this.contractGenerated = $event;
    this.quoteRequestState = {...this.quoteRequestState, status: QuoteRequestStatus.PendingContract};
    if (this.data.contractType === 'DocuSign') this.next();
  }

  async onCompleteUpload(uploadedFiles) {
    const f = uploadedFiles[0];
    if (f === undefined) {
      return;
    }

    this.showContractNotUploadedLabel = false;
    this.contractGenerated = true;
    this.signedContractDocuments = [f.data];

    await this.attachPdfContract();
  }

  onDeleteUpload(deleteResults) {
    const index = this.signedContractDocuments.findIndex((f) => deleteResults.fileId === f.fileId);
    this.signedContractDocuments.splice(index, 1);
  }

  private async validateForm(schema: any, data: any): Promise<boolean> {

    const validateSchmaId = schema.$id || 'contract.json#';

    if (this.validationSettings.getSchema(validateSchmaId)) {
      this.validationSettings.removeSchema();
    }

    const validateFn = this.validationSettings.compile(schema);
    return await validateFn(data);
  }

  next() { if (this.isFormValid) this.wizardService.next(false); }

  back() { }

  async attachPdfContract() {
    if (this.signedContractDocuments.length === 0) {
      this.showContractNotUploadedLabel = true;
    } else {
      await this.saveSignedPdfContract(this.signedContractDocuments[0]);
      LogRocket.track('contract_signed');
    }
  }

  async submitQuoteRequest() {
    this.isFormValid = await this.validateForm(this.schema, this.data);
    if (this.signedContractDocuments.length === 0) {
      this.showContractNotUploadedLabel = true;
    } else if (this.isFormValid) {
      await this.quoteRequestService.submitQuoteRequest(this.quoteRequestId).toPromise();
      LogRocket.track('contract_submitted');
      this.next();
    }
  }

  async save(): Promise<any> {
    this.loading = true;

    if (this.data && !this.isReadOnly) {
      if (this.isNewContract) {
        await this.contractService.createContract(this.quoteRequestId, this.data, this.schemaId);
        this.isNewContract = false;
      } else {
        await this.contractService.updateContract(this.quoteRequestId, this.data, this.schemaId);
      }
    }

    this.loading = false;
    return true;
  }



  async saveSignedPdfContract(file: UdFile) {
    await this.quoteRequestService.saveSignedContractDocument(this.quoteRequestId, file.fileId, 'Pdf').toPromise();
  }

  isCreditCheckValidated(): boolean {
    return this.isCreditCheckQuote && !this.successfulCreditCheck;
  }

  searchForState(zipCode: string) {
    this.lookup$ = this.usLookup.lookupStateFromZipCode(zipCode).subscribe(
      (results) => {
        this.data.billingAddress.state = results.stateCode;
        this.data.billingAddress.city = results.city;
        // Resets the data property to update the UI
        this.data = { ...this.data };
      },
      (err) => {
        this.logger.error(err);
      }
    );
    this.lookup$.unsubscribe();
  }

}
